import { useRef } from 'react';

import { Download, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

import type { PostCallWorkflow } from '@repo/shared-types';

interface ImportExportToolbarProps {
  onImport: (workflow: PostCallWorkflow) => void;
  onExport: () => void;
}

export const ImportExportToolbar = ({ onImport, onExport }: ImportExportToolbarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (
          !json.nodes ||
          !Array.isArray(json.nodes) ||
          !json.edges ||
          !Array.isArray(json.edges)
        ) {
          toast({
            variant: 'destructive',
            title: 'Invalid workflow JSON',
            description: 'File must contain nodes and edges arrays',
          });
          return;
        }
        const hasStart = json.nodes.some((n: { type: string }) => n.type === 'start');
        if (!hasStart) {
          toast({
            variant: 'destructive',
            title: 'Invalid workflow',
            description: 'Workflow must have a start node',
          });
          return;
        }
        onImport(json as PostCallWorkflow);
        toast({
          variant: 'success',
          title: 'Workflow imported',
          description: `Loaded ${json.nodes.length} nodes`,
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Invalid JSON',
          description: 'Could not parse the file',
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="flex gap-1">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileSelect}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={14} className="mr-1" /> Import
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onExport}>
        <Download size={14} className="mr-1" /> Export
      </Button>
    </div>
  );
};
